import type { Atom } from 'nanostores';

import { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger.js';
import { truthyPredicate } from '@iconik-io/core/src/truthy_predicate';

import { cx } from '../../shared.js';
import { SubscribersList } from '../../state/subscribers_list.js';

import styles from './waveform.module.css';

const logger = DebugLogger.getInstance('unified_player_waveform');

type DecodeAudioChunkResult = {
  channelsData: Float32Array[];
  numSamples: number;
};

const waveformGlyph = (size: number, className = '') => `
  <svg aria-hidden="true" class="${className}" xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" fill="none" viewBox="0 0 24 24"><path fill="currentColor" d="M12 8a1 1 0 0 0-1 1v6c0 .6.4 1 1 1s1-.4 1-1V9c0-.6-.4-1-1-1Zm4-3a1 1 0 0 0-1 1v12c0 .6.4 1 1 1s1-.4 1-1V6c0-.5-.4-1-1-1Zm4 5a1 1 0 0 0-1 1v2c0 .6.4 1 1 1s1-.4 1-1v-2c0-.6-.4-1-1-1ZM8 10a1 1 0 0 0-1 1v2c0 .6.5 1 1 1 .6 0 1-.4 1-1v-2c0-.6-.4-1-1-1ZM4 9a1 1 0 0 0-1 1v4a1 1 0 0 0 2 0v-4c0-.6-.5-1-1-1Z"/></svg>
`;

/**
 * Renders the "no waveform" state into the player container and returns it, so
 * the caller can remove it again. Lives here to sit with the loader's other
 * three states, which share its markup and styles.
 */
export function showWaveformUnavailableMessage(
  container: HTMLElement,
): HTMLElement {
  const messageContainer = document.createElement('div');
  messageContainer.className = cx([
    styles.message_container,
    styles.unavailable_container,
  ]);
  messageContainer.setAttribute('role', 'status');
  // Attach the live region before filling it, or it lands already populated and
  // screen readers have no change to announce
  container.prepend(messageContainer);
  messageContainer.innerHTML = `
    ${waveformGlyph(40)}
    <p class="${styles.message_text} ${styles.unavailable_title}">Waveform unavailable for this subclip</p>
    <p class="${styles.message_text} ${styles.unavailable_hint}">Use the player controls to scrub</p>
  `;
  return messageContainer;
}

const MAX_AUTO_LOAD_DURATION = 30 * 60; // 30 minutes in seconds
const CHUNK_SIZE = 256 * 1024; // 256kb
const MAX_MP3_FRAME_SIZE = 1450; // 1441 bytes is the maximum size of an MP3 frame, I'm padding it a bit to be safe
const SAMPLE_RATE = 8000;

export class WaveformLoader {
  #url: string | undefined;
  #loaderElement: Element | undefined;
  #errorElement: Element | undefined;
  #loadWaveformButton: HTMLButtonElement | undefined;
  #container: HTMLElement;
  #onDataDecoded: (channelsData: Float32Array[], totalSamples: number) => void;
  #duration: Atom<number | undefined>;
  #abortController: AbortController = new AbortController();
  #audioContext: AudioContext | undefined;
  #subscribersList = new SubscribersList();
  #loadingStarted = false;
  #incompleteFrameBuffer: Uint8Array | undefined;
  constructor(
    container: HTMLElement,
    dataLoadedCallback: (
      channelsData: Float32Array[],
      totalSamples: number,
    ) => void,
    duration: Atom<number | undefined>,
  ) {
    this.#container = container;
    this.#onDataDecoded = dataLoadedCallback;
    this.#duration = duration;
    this.#subscribersList.listen(this.#duration, this.#handleDurationChange);
  }

  setUrl(url: string) {
    this.#url = url;
    this.#handleDurationChange();
  }

  #handleDurationChange = () => {
    if (this.#loadingStarted) {
      return;
    }
    const duration = this.#duration.get();
    if (duration && duration <= MAX_AUTO_LOAD_DURATION) {
      this.loadWaveform();
    } else {
      this.showLoadButton();
    }
  };

  showLoadButton() {
    if (this.#loadWaveformButton) {
      return;
    }

    this.#loadWaveformButton = document.createElement('button');
    this.#loadWaveformButton.innerHTML = `
      ${waveformGlyph(24, styles.load_button_icon)}
      Generate Waveform
    `;
    this.#loadWaveformButton.className = cx([
      styles.load_button,
      styles.message_text,
      styles.message_container,
    ]);

    this.#container.appendChild(this.#loadWaveformButton);

    this.#loadWaveformButton.addEventListener(
      'click',
      async () => {
        this.#loadWaveformButton?.remove();
        this.loadWaveform();
      },
      { once: true },
    );
  }

  #showLoader() {
    this.#hideMessageElements();
    const loaderHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="none">
        <path class="${styles.loader_line}" fill="#999" d="M6.667 15.003c-.917 0-1.667.75-1.667 1.666v6.667c0 .917.75 1.667 1.667 1.667.916 0 1.666-.75 1.666-1.667v-6.667c0-.916-.75-1.666-1.666-1.666Z"/>
        <path class="${styles.loader_line}" fill="#999" d="M13.333 16.67c-.916 0-1.666.75-1.666 1.666v3.333c0 .917.75 1.667 1.666 1.667.917 0 1.667-.75 1.667-1.667v-3.333c0-.917-.75-1.667-1.667-1.667Z"/>
        <path class="${styles.loader_line}" fill="#999" d="M20 13.336c-.917 0-1.667.75-1.667 1.667v10c0 .916.75 1.666 1.667 1.666s1.667-.75 1.667-1.666v-10c0-.917-.75-1.667-1.667-1.667Z"/>
        <path class="${styles.loader_line}" fill="#999" d="M26.667 8.336c-.917 0-1.667.75-1.667 1.667v20c0 .916.75 1.666 1.667 1.666.916 0 1.666-.75 1.666-1.666v-20c0-.917-.75-1.667-1.666-1.667Z"/>
        <path class="${styles.loader_line}" fill="#999" d="M33.333 16.67c-.916 0-1.666.75-1.666 1.666v3.333c0 .917.75 1.667 1.666 1.667.917 0 1.667-.75 1.667-1.667v-3.333c0-.917-.75-1.667-1.667-1.667Z"/>
      </svg>
      <p class="${styles.message_text}">Generating waveform...</p>
    `;

    const loaderContainer = document.createElement('div');
    loaderContainer.classList.add(styles.message_container);
    loaderContainer.innerHTML = loaderHTML;
    this.#loaderElement = loaderContainer;
    this.#container.prepend(this.#loaderElement);
  }

  #showErrorMessage() {
    this.#hideMessageElements();
    const errorHtml = `
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20.19 15.5301L14.61 5.61008C14.07 4.65008 13.09 4.08008 12 4.08008C10.91 4.08008 9.92003 4.65008 9.39003 5.61008L3.81003 15.5301C3.28003 16.4701 3.29003 17.5901 3.83003 18.5201C4.37003 19.4501 5.34003 20.0101 6.42003 20.0101H17.58C18.66 20.0101 19.63 19.4501 20.17 18.5201C20.71 17.5901 20.72 16.4701 20.19 15.5301ZM18.44 17.5101C18.35 17.6601 18.09 18.0101 17.58 18.0101H6.42003C5.90003 18.0101 5.64003 17.6601 5.56003 17.5101C5.48003 17.3601 5.30003 16.9701 5.56003 16.5101L11.13 6.59008C11.39 6.13008 11.83 6.08008 12 6.08008C12.17 6.08008 12.61 6.13008 12.87 6.59008L18.45 16.5101C18.7 16.9601 18.53 17.3601 18.45 17.5101H18.44Z" fill="currentColor"/>
        <path d="M12 13.0001C12.55 13.0001 13 12.5501 13 12.0001V9.00008C13 8.45008 12.55 8.00008 12 8.00008C11.45 8.00008 11 8.45008 11 9.00008V12.0001C11 12.5501 11.45 13.0001 12 13.0001Z" fill="currentColor"/>
        <path d="M12 17.0001C12.8285 17.0001 13.5 16.3285 13.5 15.5001C13.5 14.6717 12.8285 14.0001 12 14.0001C11.1716 14.0001 10.5 14.6717 10.5 15.5001C10.5 16.3285 11.1716 17.0001 12 17.0001Z" fill="currentColor"/>
      </svg>
      <p class="${styles.message_text} ${styles.error_text}">Failed to generate waveform</p>
    `;

    const errorContainer = document.createElement('div');
    errorContainer.className = cx([
      styles.message_container,
      styles.error_text,
    ]);
    errorContainer.innerHTML = errorHtml;
    this.#errorElement = errorContainer;
    this.#container.prepend(errorContainer);
  }

  async loadWaveform() {
    const duration = this.#duration.get();
    if (!this.#url || !duration || this.#loadingStarted) {
      return;
    }
    logger.debug('Starting waveform load for URL:', this.#url);

    this.#loadingStarted = true;

    this.#showLoader();
    this.#audioContext = new AudioContext({
      latencyHint: 'playback',
      sampleRate: SAMPLE_RATE,
    });

    try {
      const response = await this.#fetchAudioFile(this.#url);
      if (!response) {
        return;
      }
      const contentType = response.headers.get('content-type') ?? 'unknown';
      const chunkableContentTypes = [
        'audio/mpeg',
        'audio/mp3',
        'audio/mpeg3',
        'audio/x-mpeg-3',
      ];
      const isChunkable =
        chunkableContentTypes.includes(contentType) ||
        (contentType === 'unknown' &&
          (await this.#detectFileTypeFromMagicBytes(response)));

      if (isChunkable) {
        await this.#handleChunkableFile(response);
      } else {
        await this.handleNonChunkableFile(response);
      }

      this.#hideMessageElements();
      logger.debug('Waveform loaded successfully');
    } catch (error) {
      logger.debug('Failed to load waveform:', error);
      this.#handleError(error);
    } finally {
      await this.#cleanup();
    }
  }

  async #fetchAudioFile(url: string) {
    const response = await fetch(url, {
      signal: this.#abortController.signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response;
  }

  // Non chunkable files (OGG, FLAC, etc.) has to be processed in one piece, doesn't support being chunked before processing
  async handleNonChunkableFile(response: Response) {
    const arrayBuffer = await response.arrayBuffer();
    const { channelsData, numSamples } =
      await this.#decodeAudioChunk(arrayBuffer);
    this.#onDataDecoded(channelsData, numSamples);
  }

  // MP3 and WAV files can and should be chunked for processing
  async #handleChunkableFile(response: Response) {
    if (!response.body) {
      throw new Error('ReadableStream not supported');
    }
    const reader = response.body.getReader();
    const contentLength = Number(response.headers.get('Content-Length'));

    let totalBytesReceived = 0;
    let networkChunks: Uint8Array[] = [];
    let networkChunksBytes = 0;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        networkChunks.push(value);
        networkChunksBytes += value.length;
        totalBytesReceived += value.length;

        const remainingBytes = contentLength - totalBytesReceived;

        const isLastNetworkChunk = totalBytesReceived === contentLength;
        // Process the chunk if it's at least CHUNK_SIZE or it's the last chunk
        // Avoids the last chunk being too small by having a minimum remaining size of CHUNK_SIZE * 2
        const shouldProcessNetworkChunk =
          (networkChunksBytes >= CHUNK_SIZE &&
            remainingBytes >= CHUNK_SIZE * 2) ||
          isLastNetworkChunk;

        if (shouldProcessNetworkChunk) {
          await this.#processNetworkChunks(networkChunks, isLastNetworkChunk);
          networkChunks = [];
          networkChunksBytes = 0;
        }
      }
    } finally {
      reader.releaseLock();
      this.#incompleteFrameBuffer = undefined;
    }
  }

  async #processNetworkChunks(
    networkChunks: Uint8Array[],
    isLastNetworkChunk: boolean,
  ): Promise<void> {
    const concatenatedData = this.#concatenateUint8Arrays(networkChunks);

    // Split into an even size, minimum CHUNK_SIZE in size
    const totalSize = concatenatedData.length;
    const numAudioChunks = Math.max(1, Math.floor(totalSize / CHUNK_SIZE));
    const audioChunkSize = Math.floor(totalSize / numAudioChunks);
    const audioChunks = [];

    for (let i = 0; i < numAudioChunks; i++) {
      audioChunks.push(
        concatenatedData.slice(i * audioChunkSize, (i + 1) * audioChunkSize),
      );
    }

    for (let i = 0; i < audioChunks.length; i++) {
      const currentAudioChunk = audioChunks[i];
      const isLastOfAllAudioChunks =
        i === audioChunks.length - 1 && isLastNetworkChunk;
      const { completeData, remainingData } = this.#splitChunkAtLastFrame(
        currentAudioChunk,
        isLastOfAllAudioChunks,
      );
      const processData = this.#concatenateUint8Arrays(
        [this.#incompleteFrameBuffer, completeData].filter(truthyPredicate),
      );
      this.#incompleteFrameBuffer = remainingData;
      await this.#processAudioChunk(processData);
    }
  }

  #concatenateUint8Arrays(arrays: Uint8Array[]): Uint8Array<ArrayBuffer> {
    const totalLength = arrays.reduce((acc, arr) => acc + arr.length, 0);
    const result = new Uint8Array(totalLength);
    let offset = 0;

    for (const arr of arrays) {
      result.set(arr, offset);
      offset += arr.length;
    }

    return result;
  }

  async #processAudioChunk(chunk: Uint8Array<ArrayBuffer>) {
    if (!this.#audioContext) {
      return;
    }

    try {
      const duration = this.#duration.get() ?? 0;
      const { channelsData } = await this.#decodeAudioChunk(chunk.buffer);
      const totalSamples = Math.ceil(duration * this.#audioContext.sampleRate);

      this.#onDataDecoded(channelsData, totalSamples);
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: debug
      console.error('Error processing chunk:', error);
    }
  }

  #handleError(error: unknown) {
    if (error instanceof Error && error.name === 'AbortError') {
      logger.debug('Waveform loading aborted');
    } else {
      logger.debug('Error loading waveform:', error);
      this.#showErrorMessage();
    }
  }

  async #cleanup() {
    await this.#audioContext?.close();
    this.#audioContext = undefined;
  }

  async #decodeAudioChunk(
    arrayBuffer: ArrayBuffer,
  ): Promise<DecodeAudioChunkResult> {
    if (!this.#audioContext) {
      throw new Error('AudioContext not initialized');
    }

    const decodedData = await this.#audioContext.decodeAudioData(arrayBuffer);
    const channelsData: Float32Array[] = [];
    const numSamples = decodedData.length;

    for (let i = 0; i < decodedData.numberOfChannels; i++) {
      channelsData.push(decodedData.getChannelData(i));
    }
    return { channelsData, numSamples };
  }

  // Split at the last frame, unless it's the last chunk
  #splitChunkAtLastFrame(
    chunkData: Uint8Array,
    isLastChunk: boolean,
  ): {
    completeData: Uint8Array;
    remainingData: Uint8Array;
  } {
    let lastFrameIndex = -1;

    for (let i = chunkData.length - MAX_MP3_FRAME_SIZE; i >= 3; i--) {
      if (this.#isValidMp3Header(chunkData, i)) {
        lastFrameIndex = i;
        break;
      }
    }

    if (lastFrameIndex === -1 || isLastChunk) {
      return {
        completeData: chunkData,
        remainingData: new Uint8Array(0),
      };
    }

    return {
      completeData: chunkData.slice(0, lastFrameIndex),
      remainingData: chunkData.slice(lastFrameIndex),
    };
  }

  // Parses the bytestream at the current offset to check if it's a valid MP3 frame header
  // http://www.mp3-tech.org/programmer/frame_header.html for information about the header structure
  // We are assuming that it's a Layer I MP3 file, which all of our proxies should be.
  #isValidMp3Header(uint8Array: Uint8Array, offset: number) {
    if (offset + 4 > uint8Array.length) {
      return false; // Not enough bytes for a header
    }

    // First byte must be 0xFF
    const byte1 = uint8Array[offset];
    if (byte1 !== 0xff) {
      return false;
    }

    // Second byte must be one of the valid frame sync values
    const byte2 = uint8Array[offset + 1];
    if (byte2 !== 0xfb && byte2 !== 0xfa) {
      return false;
    }

    // Check bitrate index (first 4 bits of byte3)
    const byte3 = uint8Array[offset + 2];
    const bitrateIndex = (byte3 & 0b11110000) >>> 4;

    // Bitrate indexes for easier readability
    const bitrates: Record<number, number> = {
      0b0001: 32000,
      0b0010: 40000,
      0b0011: 48000,
      0b0100: 56000,
      0b0101: 64000,
      0b0110: 80000,
      0b0111: 96000,
      0b1000: 112000,
      0b1001: 128000,
      0b1010: 160000,
      0b1011: 192000,
      0b1100: 224000,
      0b1101: 256000,
      0b1110: 320000,
    };
    const bitrate = bitrates[bitrateIndex];
    if (bitrate === undefined) {
      return false; // Invalid or free bitrate
    }

    // Check sampling rate index (bits 5,6 of byte3)
    const samplingRateIndex = (byte3 & 0b00001100) >>> 2;
    const sampleRates: Record<number, number> = {
      0b00: 44100,
      0b01: 48000,
      0b10: 32000,
    };
    const sampleRate = sampleRates[samplingRateIndex];
    if (sampleRate === undefined) {
      return false;
    }

    // Calculate frame length
    // Frame length = (144 * bitrate) / sampleRate + padding
    const isPadded = (byte3 & 0b00000010) >>> 1;
    const frameLength = Math.floor((144 * bitrate) / sampleRate + isPadded);

    // Check if we can read the next frame header
    if (offset + frameLength + 2 >= uint8Array.length) {
      return false;
    }

    // Verify next frame starts with sync word (0xFF) and valid second sync byte (0xFB or 0xFA)
    const nextFrameFirstByte = uint8Array[offset + frameLength];
    const nextFrameSecondByte = uint8Array[offset + frameLength + 1];
    return (
      nextFrameFirstByte === 0xff &&
      (nextFrameSecondByte === 0xfb || nextFrameSecondByte === 0xfa)
    );
  }

  hideLoader() {
    this.#loaderElement?.remove();
    this.#loaderElement = undefined;
  }

  #hideMessageElements() {
    this.#loaderElement?.remove();
    this.#loaderElement = undefined;
    this.#errorElement?.remove();
    this.#errorElement = undefined;
    this.#loadWaveformButton?.remove();
    this.#loadWaveformButton = undefined;
  }

  destroy() {
    this.#abortController?.abort();
    this.#hideMessageElements();
    this.#subscribersList.unsubscribeAll();
    this.#cleanup();
  }

  async #detectFileTypeFromMagicBytes(response: Response): Promise<boolean> {
    // Clone the response to not disturb the original stream
    const clonedResponse = response.clone();
    const reader = clonedResponse.body?.getReader();
    if (!reader) {
      return false;
    }

    try {
      // 3 bytes is enough for MP3 detection
      // File signature information from https://en.wikipedia.org/wiki/List_of_file_signatures
      const { value } = await reader.read();
      if (!value || value.length < 3) {
        return false;
      }

      // MP3 magic bytes: starts with 49 44 33: "ID3" or FF FB / FF FA
      const isMP3 =
        (value[0] === 0x49 && value[1] === 0x44 && value[2] === 0x33) || // 'ID3'
        (value[0] === 0xff && (value[1] === 0xfb || value[1] === 0xfa)); // MPEG frame sync

      return isMP3;
    } finally {
      reader.releaseLock();
    }
  }
}
